import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ExternalLink, ChevronDown, ChevronUp } from 'lucide-react'
import type { SearchResult } from '../lib/types'
import { getPaperSongUrl, getSunoSync } from '../lib/data'
import { LatexText } from '../lib/latex'
import { usePlayer } from '../lib/player'
import ShareButton from './ShareButton'

interface PaperCardProps {
  result: SearchResult
  query?: string
}

function highlightText(text: string, query: string): string {
  if (!query.trim()) return text
  
  const terms = query.trim().split(/\s+/).filter(t => t.length > 2)
  if (terms.length === 0) return text
  
  const regex = new RegExp(`(${terms.map(escapeRegex).join('|')})`, 'gi')
  return text.replace(regex, '<mark class="bg-accent-400/20 text-accent-200 rounded px-0.5">$1</mark>')
}

function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

export default function PaperCard({ result, query = '' }: PaperCardProps) {
  const [expanded, setExpanded] = useState(false)
  const url = getPaperSongUrl(result.id)
  const suno = getSunoSync(result.id)
  const coverUrl = suno?.image_url
  const { currentPaperId, isPlaying, play, toggle } = usePlayer()
  
  const isCurrentSong = currentPaperId === result.id
  const isThisPlaying = isCurrentSong && isPlaying

  // Show ~300 chars for 2.5 lines
  const abstractPreview = result.abstract?.slice(0, 300) || ''
  const hasMoreAbstract = result.abstract && result.abstract.length > 300

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't play if clicking on interactive elements
    const target = e.target as HTMLElement
    if (target.closest('a, button')) return
    
    e.preventDefault()
    if (isCurrentSong) {
      toggle()
    } else {
      play(result.id)
    }
  }

  const handleExpandClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setExpanded(!expanded)
  }

  return (
    <article 
      onClick={handleCardClick}
      className="glass-card rounded-xl p-4 hover:border-surface-600/70 transition-all cursor-pointer group"
    >
      <div className="flex gap-4">
        {/* Cover art with play indicator */}
        <div className="flex-shrink-0 relative w-24 h-24 rounded-lg overflow-hidden">
          {coverUrl ? (
            <img
              src={coverUrl}
              alt={result.title}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full bg-surface-700 shimmer flex items-center justify-center">
              <svg className="w-8 h-8 text-surface-500" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
              </svg>
            </div>
          )}
          
          {/* Play/Pause overlay */}
          <div className={`absolute inset-0 flex items-center justify-center bg-black/40 transition-opacity ${
            isThisPlaying ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
          }`}>
            {isThisPlaying ? (
              <div className="playing-bars flex items-end gap-0.5 h-4">
                <span></span>
                <span></span>
                <span></span>
              </div>
            ) : (
              <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5v14l11-7z"/>
              </svg>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Title - clicking plays the song */}
          <h2 className="text-base font-semibold text-surface-100 leading-snug line-clamp-2">
            <LatexText>{result.title}</LatexText>
          </h2>

          {/* Authors */}
          <p 
            className="text-surface-400 text-sm line-clamp-1 mt-0.5"
            dangerouslySetInnerHTML={{ __html: highlightText(result.authors, query) }}
          />

          {/* Abstract with fade */}
          {result.abstract && (
            <div className="mt-1.5">
              <div 
                className={`text-surface-300 text-sm leading-relaxed ${
                  !expanded ? 'abstract-fade max-h-[3.75rem] overflow-hidden' : ''
                }`}
              >
                <LatexText as="span">
                  {expanded ? result.abstract : abstractPreview + (hasMoreAbstract ? '...' : '')}
                </LatexText>
              </div>
            </div>
          )}

          {/* Bottom row: More + buttons */}
          <div className="flex items-center justify-between mt-2">
            <div>
              {hasMoreAbstract && (
                <button
                  onClick={handleExpandClick}
                  className="text-accent-400 hover:text-accent-300 text-xs font-medium inline-flex items-center gap-0.5 transition-colors"
                >
                  {expanded ? (
                    <>
                      <ChevronUp size={14} />
                      Less
                    </>
                  ) : (
                    <>
                      <ChevronDown size={14} />
                      More
                    </>
                  )}
                </button>
              )}
            </div>
            <div className="flex items-center gap-1">
              <ShareButton paperId={result.id} />
              <Link
                to={url}
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 h-8 rounded-lg text-sm text-surface-300 hover:text-surface-100 bg-surface-800 hover:bg-surface-700 transition-colors"
              >
                <ExternalLink size={14} />
                <span>Song page</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </article>
  )
}
